import * as Sentry from '@sentry/react'
import './i18n'
import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import App from './App'
import { routePreloaders, preloadNotFound } from './pages/lazyPages'
import { normalizePath } from './seo/routeMeta'
import './index.css'
import './styles/home.css'

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    release: `website@${import.meta.env.VITE_SENTRY_RELEASE || import.meta.env.VITE_APP_VERSION || '1.0.0'}`,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllInputs: true }),
    ],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    sendDefaultPii: false,
    // Ad blockers routinely abort adsbygoogle / googlesyndication requests.
    // Those are expected noise, not product failures — drop them before ERROR.
    ignoreErrors: [
      /adsbygoogle/i,
      /googlesyndication/i,
      /doubleclick\.net/i,
      /ERR_BLOCKED_BY_CLIENT/i,
      /blocked.?(by)?.?ad/i,
      /net::ERR_BLOCKED_BY_CLIENT/i,
    ],
    denyUrls: [
      /pagead2\.googlesyndication\.com/i,
      /googleads\.g\.doubleclick\.net/i,
      /googlesyndication\.com/i,
    ],
    beforeSend(event) {
      const haystack = [
        event.message,
        event.exception?.values?.map((v) => `${v.type ?? ''} ${v.value ?? ''}`).join(' '),
        ...(event.breadcrumbs?.map((b) => `${b.category ?? ''} ${b.message ?? ''} ${b.data?.url ?? ''}`) ?? []),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()

      if (
        /adsbygoogle|googlesyndication|doubleclick\.net|pagead2|err_blocked_by_client|blocked.?ad|adblock/.test(
          haystack,
        )
      ) {
        return null
      }
      return event
    },
  });
}

// Prevent browser extension interference
if (typeof window !== 'undefined') {
  // Suppress common extension-related console errors
  const originalError = console.error;
  console.error = (...args) => {
    const message = args[0]?.toString() || '';
    if (message.includes('Could not establish connection') || 
        message.includes('Receiving end does not exist') ||
        message.includes('Extension context invalidated') ||
        message.includes('chrome-extension://') ||
        message.includes('moz-extension://') ||
        message.includes('safari-extension://')) {
      return; // Suppress these specific errors
    }
    originalError.apply(console, args);
  };
  
  // Also suppress unhandled promise rejections from extensions
  const originalUnhandledRejection = window.onunhandledrejection;
  window.onunhandledrejection = (event) => {
    const message = event.reason?.toString() || '';
    if (message.includes('Could not establish connection') || 
        message.includes('Receiving end does not exist') ||
        message.includes('Extension context invalidated')) {
      event.preventDefault();
      return;
    }
    if (originalUnhandledRejection) {
      originalUnhandledRejection.call(window, event);
    }
  };
}

const render = () =>
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <Sentry.ErrorBoundary fallback={<p>Something went wrong. Please refresh the page.</p>}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </Sentry.ErrorBoundary>
    </React.StrictMode>,
  )

// Pages are prerendered to static HTML at build time (scripts/prerender-routes.mjs).
// Load the current route's chunk before the first render so React swaps the
// static markup for the live page in one commit, with no spinner in between.
const firstPath = normalizePath(window.location.pathname)
const preloadFirstRoute = firstPath === '/' ? undefined : (routePreloaders[firstPath] ?? preloadNotFound)
if (preloadFirstRoute) {
  preloadFirstRoute().catch(() => undefined).finally(render)
} else {
  render()
}